import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { jsPDF } from "jspdf";
import moment from "moment";
import { resolveFileUrl } from "@/utils";
import {
  CheckCircle2, AlertTriangle, Download, FileText,
  ArrowLeft, Package, ChevronDown, ChevronUp, Eye, EyeOff
} from "lucide-react";

const STATUS_CFG = {
  draft:         { label: "Esborrany",         className: "bg-gray-100 text-gray-700" },
  pending_review:{ label: "Pendent revisió",   className: "bg-amber-100 text-amber-700" },
  reviewed:      { label: "Revisat",           className: "bg-blue-100 text-blue-700" },
  confirmed:     { label: "Confirmat",         className: "bg-emerald-100 text-emerald-700" },
  stock_updated: { label: "Estoc actualitzat", className: "bg-purple-100 text-purple-700" },
};

function safeParseJSON(str) {
  try { return JSON.parse(str || "[]"); } catch { return []; }
}

function SectionToggle({ title, icon: SectionIcon, count, show, onToggle, children }) {
  const Icon = SectionIcon;
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-sm font-semibold text-gray-700 transition-colors"
      >
        <span className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" />}
          {title} {count !== undefined && <span className="text-gray-400 font-normal">({count})</span>}
        </span>
        {show ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {show && <div>{children}</div>}
    </div>
  );
}

function generatePDF(invoice, items, taxLines, movements) {
  const doc = new jsPDF();
  const margin = 14;
  let y = 14;
  const PAGE_H = 290;

  const checkPage = (needed = 10) => {
    if (y + needed > PAGE_H) { doc.addPage(); y = 20; }
  };

  // ─── Header band ───────────────────────────────────────────────────────────
  doc.setFillColor(123, 45, 63);
  doc.rect(0, 0, 210, 34, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont(undefined, "bold");
  doc.text("FACTURA", margin, 16);
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.text(`Nº ${invoice.invoice_number || "—"}`, margin, 26);
  doc.text(`Data: ${invoice.invoice_date || "—"}`, 120, 26);
  doc.setFontSize(8);
  doc.setTextColor(200, 180, 185);
  doc.text(`Generat per Ho-Sys el ${moment().format("DD/MM/YYYY HH:mm")}`, margin, 32);
  y = 46;

  // ─── Supplier info ──────────────────────────────────────────────────────────
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(9);
  doc.setFont(undefined, "bold");
  doc.text("PROVEÏDOR", margin, y);
  y += 6;
  doc.setFont(undefined, "normal");
  doc.setFontSize(10);
  doc.text(invoice.supplier_name || "—", margin, y); y += 6;
  if (invoice.supplier_nif) {
    doc.setFontSize(9);
    doc.text(`NIF/CIF: ${invoice.supplier_nif}`, margin, y); y += 5;
  }
  if (invoice.payment_terms) {
    doc.text(`Condicions pag.: ${invoice.payment_terms}`, margin, y); y += 5;
  }
  y += 4;

  // ─── Tax lines ──────────────────────────────────────────────────────────────
  if (taxLines.length > 0) {
    checkPage(taxLines.length * 8 + 16);
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.setFillColor(245, 245, 248);
    doc.rect(margin, y, 182, 8, "F");
    doc.setTextColor(90, 90, 90);
    const taxHeaders = ["Base (€)", "% IVA", "Quota IVA (€)", "Recàrrec %", "Recàrrec €"];
    taxHeaders.forEach((h, i) => doc.text(h, margin + i * 36, y + 5.5));
    y += 11;
    doc.setFont(undefined, "normal");
    doc.setTextColor(30, 30, 30);
    taxLines.forEach(tl => {
      checkPage(8);
      [tl.base, tl.vat_pct, tl.vat_amount, tl.surcharge_pct || 0, tl.surcharge_amount || 0].forEach((v, i) => {
        doc.text(String(typeof v === "number" ? v.toFixed(2) : v ?? "—"), margin + i * 36, y);
      });
      y += 7;
    });
    y += 4;
  }

  // ─── Totals box ─────────────────────────────────────────────────────────────
  checkPage(40);
  const totals = [
    ["Base imponible:", `${(invoice.subtotal || 0).toFixed(2)} €`],
    ["Total IVA:", `${(invoice.vat_amount || 0).toFixed(2)} €`],
    ...(invoice.surcharge_amount ? [["Recàrrec equiv.:", `${invoice.surcharge_amount.toFixed(2)} €`]] : []),
    ...(invoice.retention_amount ? [["Retenció:", `-${invoice.retention_amount.toFixed(2)} €`]] : []),
  ];
  totals.forEach(([label, val]) => {
    doc.setFontSize(9); doc.setFont(undefined, "normal"); doc.setTextColor(60, 60, 60);
    doc.text(label, 125, y);
    doc.text(val, 196, y, { align: "right" });
    y += 7;
  });
  doc.setFillColor(123, 45, 63);
  doc.rect(110, y - 4, 86, 10, "F");
  doc.setFontSize(11); doc.setFont(undefined, "bold"); doc.setTextColor(255, 255, 255);
  doc.text("TOTAL:", 125, y + 3.5);
  doc.text(`${(invoice.total || 0).toFixed(2)} €`, 196, y + 3.5, { align: "right" });
  y += 18;

  // ─── Item lines ─────────────────────────────────────────────────────────────
  if (items.length > 0) {
    checkPage(16 + items.length * 8);
    doc.setFontSize(9); doc.setFont(undefined, "bold");
    doc.setFillColor(245, 245, 248);
    doc.rect(margin, y - 4, 182, 8, "F");
    doc.setTextColor(90, 90, 90);
    const cols = [14, 88, 104, 118, 150, 184];
    ["Descripció", "Qttat", "Unitat", "Preu u.", "Total", "IVA%"].forEach((h, i) => doc.text(h, cols[i], y));
    y += 8;
    doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30); doc.setFontSize(8);
    items.forEach(item => {
      checkPage(8);
      doc.text(doc.splitTextToSize(item.description || "—", 68)[0], cols[0], y);
      doc.text(String(item.quantity ?? ""), cols[1], y);
      doc.text(item.unit || "", cols[2], y);
      doc.text(`${(item.unit_price || 0).toFixed(2)}`, cols[3], y);
      doc.text(`${(item.line_total || 0).toFixed(2)}`, cols[4], y);
      doc.text(`${item.vat_pct || 0}%`, cols[5], y);
      y += 7;
    });
  }

  // ─── Footer ─────────────────────────────────────────────────────────────────
  const totalPages = doc.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7); doc.setTextColor(170, 170, 170);
    doc.text(`Pàgina ${i} de ${totalPages}`, 196, 287, { align: "right" });
    doc.text(`Ho-Sys · AEH Reus`, margin, 287);
  }

  doc.save(`factura_${invoice.invoice_number || invoice.id}.pdf`);
}

export default function InvoiceDetail({ invoice, movements, onBack }) {
  const [showItems, setShowItems]     = useState(true);
  const [showTax, setShowTax]         = useState(true);
  const [showMovements, setShowMovements] = useState(true);
  const [showImage, setShowImage]     = useState(false);

  const items    = safeParseJSON(invoice.items);
  const taxLines = safeParseJSON(invoice.tax_lines);
  const status   = STATUS_CFG[invoice.status] || STATUS_CFG.draft;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-700 mb-2 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Tornar al llistat
          </button>
          <h2 className="text-xl font-bold text-gray-900">
            {invoice.supplier_name || "Factura sense proveïdor"}
          </h2>
          <p className="text-sm text-gray-500">
            Nº {invoice.invoice_number || "—"} · {invoice.invoice_date || "—"}
            {invoice.supplier_nif && <span className="ml-2 text-gray-400">· {invoice.supplier_nif}</span>}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge className={`${status.className} border-0`}>{status.label}</Badge>
          <Button
            onClick={() => generatePDF(invoice, items, taxLines, movements)}
            size="sm" variant="outline" className="gap-2 text-xs"
          >
            <Download className="w-3.5 h-3.5" /> PDF
          </Button>
        </div>
      </div>

      {/* VAT warning / ok */}
      {invoice.vat_valid === false && invoice.vat_warning && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-700">{invoice.vat_warning}</p>
        </div>
      )}
      {invoice.vat_valid === true && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-emerald-700 font-medium">IVA validat correctament</p>
        </div>
      )}

      {/* Summary grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Proveïdor",      value: invoice.supplier_name },
          { label: "NIF/CIF",        value: invoice.supplier_nif },
          { label: "Nº Factura",     value: invoice.invoice_number },
          { label: "Data factura",   value: invoice.invoice_date },
          { label: "Base imponible", value: `${(invoice.subtotal || 0).toFixed(2)} €` },
          { label: "Total IVA",      value: `${(invoice.vat_amount || 0).toFixed(2)} €` },
          { label: "Recàrrec",       value: `${(invoice.surcharge_amount || 0).toFixed(2)} €` },
          { label: "TOTAL",          value: `${(invoice.total || 0).toFixed(2)} €`, highlight: true },
        ].map(f => (
          <div key={f.label} className={`rounded-xl p-3 border ${
            f.highlight ? "bg-[#7B2D3F]/5 border-[#7B2D3F]/20" : "bg-gray-50 border-gray-100"
          }`}>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{f.label}</p>
            <p className={`text-sm font-semibold mt-1 truncate ${f.highlight ? "text-[#7B2D3F] text-base" : "text-gray-900"}`}>
              {f.value || "—"}
            </p>
          </div>
        ))}
      </div>

      {/* Tax lines */}
      {taxLines.length > 0 && (
        <SectionToggle title="Línies d'IVA" count={taxLines.length} show={showTax} onToggle={() => setShowTax(!showTax)}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {["Base (€)", "% IVA", "Quota IVA (€)", "Rec. %", "Recàrrec (€)"].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-bold text-gray-400 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {taxLines.map((tl, i) => (
                  <tr key={`tl-${i}`} className="hover:bg-gray-50/50">
                    <td className="px-3 py-2 font-semibold">{tl.base?.toFixed(2)}</td>
                    <td className="px-3 py-2">
                      <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">{tl.vat_pct}%</Badge>
                    </td>
                    <td className="px-3 py-2">{tl.vat_amount?.toFixed(2)}</td>
                    <td className="px-3 py-2">{tl.surcharge_pct || 0}%</td>
                    <td className="px-3 py-2">{(tl.surcharge_amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionToggle>
      )}

      {/* Product lines */}
      {items.length > 0 && (
        <SectionToggle title="Línies de producte" icon={FileText} count={items.length} show={showItems} onToggle={() => setShowItems(!showItems)}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {["Descripció", "Qttat", "Unitat", "Preu unit.", "Total", "IVA%"].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-bold text-gray-400 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, i) => (
                  <tr key={`item-${i}`} className="hover:bg-gray-50/50">
                    <td className="px-3 py-2 font-medium max-w-[200px] truncate">{item.description}</td>
                    <td className="px-3 py-2">{item.quantity}</td>
                    <td className="px-3 py-2 text-gray-500">{item.unit}</td>
                    <td className="px-3 py-2">{item.unit_price?.toFixed(2)}€</td>
                    <td className="px-3 py-2 font-semibold">{item.line_total?.toFixed(2)}€</td>
                    <td className="px-3 py-2">
                      <Badge className="bg-blue-50 text-blue-700 border-0 text-[10px]">{item.vat_pct}%</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionToggle>
      )}

      {/* Stock movements */}
      {movements?.length > 0 && (
        <SectionToggle title="Moviments d'estoc" icon={Package} count={movements.length} show={showMovements} onToggle={() => setShowMovements(!showMovements)}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {["Producte factura", "Producte intern", "Coincidència", "Quantitat", "Preu unit."].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-bold text-gray-400 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {movements.map((m, i) => (
                  <tr key={`member-${i}`} className="hover:bg-gray-50/50">
                    <td className="px-3 py-2 font-medium">{m.invoice_product_name}</td>
                    <td className="px-3 py-2 text-gray-600">{m.matched_product_name || <span className="text-gray-400 italic text-xs">No mapejat</span>}</td>
                    <td className="px-3 py-2">
                      <Badge className={`border-0 text-xs ${(m.match_confidence || 0) >= 70 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {m.match_confidence || 0}%
                      </Badge>
                    </td>
                    <td className="px-3 py-2">{m.quantity} {m.unit}</td>
                    <td className="px-3 py-2">{m.unit_price?.toFixed(2)}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionToggle>
      )}

      {/* Scanned document */}
      {invoice.processed_file_url && (
        <div>
          <button
            onClick={() => setShowImage(!showImage)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
          >
            {showImage ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showImage ? "Amagar" : "Veure"} document original escanejat
          </button>
          {showImage && (
            <img
              src={resolveFileUrl(invoice.processed_file_url)}
              alt="Factura processada"
              className="mt-3 max-w-sm w-full rounded-xl border border-gray-200 shadow-sm"
            />
          )}
        </div>
      )}
    </div>
  );
}